import { useEffect, useState, useRef, type ReactElement } from 'react';
import { useAtomValue } from 'jotai';
import { SavedWordData, WordData } from '@shared/types/words';
import { useSpring, animated, config } from '@react-spring/web';
import Timestamp from '@app/components/Timestamp';
import { selectedMediaInfoAtom } from '@app/atoms/selectedMediaAtoms';
import MediaInfoLite from '@app/components/MediaInfoLite';
import './Card.scss';
import { ShortMediaInfo } from '@shared/types';

interface CardProps {
  data: WordData | SavedWordData;
  active: boolean;
  onMount?: (f: (() => void) | undefined) => void;
  onSave?: (card: WordData) => void;
}

enum CardAction {
  TAP = 'tap',
  SAVE = 'save',
  INFO = 'info'
}

export function Card({ data, active, onMount, onSave }: CardProps) {
  const [flipped, setFlipped] = useState(false);

  // Current Word Data
  const { word, example_phrases: examplePhrases, transcription, definition } = data;
  const uniqueTss = Array.from(new Set(examplePhrases.map((phrase) => phrase.ts).filter((ts) => ts != null)));

  const currentAction = useRef<CardAction>(CardAction.TAP);

  let shortInfo: ShortMediaInfo | null = useAtomValue(selectedMediaInfoAtom);
  if ((data as SavedWordData).savedData?.metadata) {
    shortInfo = (data as SavedWordData).savedData?.metadata || null;
  }

  const [showInfo, setShowInfo] = useState(false);
  const [wordInfoVisible, setWordInfoVisible] = useState(false);

  // Animation for word-info translateY
  const wordInfoSpring = useSpring({
    transform: showInfo ? 'translateY(0%)' : 'translateY(100%)',
    config: config.stiff,
    onStart: () => {
      if (showInfo) setWordInfoVisible(true);
    },
    onRest: () => {
      if (!showInfo) setWordInfoVisible(false);
    },
  });

  const handleSaveClick = async () => {
    if (!shortInfo || data.saved) return; // TODO: add a handling

    if (typeof onSave === 'function') {
      onSave(data as WordData);
    }
  };

  const handleInfoClick = () => {
    console.log('info click!!!', shortInfo);
    setShowInfo((state) => !state);
  };
  
  const handleAction = (action: CardAction) => {
    switch (action) {
      case CardAction.SAVE:
        handleSaveClick();
        break;
      case CardAction.INFO:
        handleInfoClick();
        break;
    }
  };

  const handleTap = () => {
    if (currentAction.current === CardAction.TAP) {
      setFlipped(state => !state);
    } else {
      handleAction(currentAction.current);
    }
  };

  useEffect(() => {
    if (typeof onMount === 'function') {
      onMount(handleTap);
      return () => {
        onMount(undefined);
      };
    }
  }, [onMount]);

  const { transform, opacity } = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(90vh) rotateX(${flipped ? 180 : 0}deg)`,
    config: config.stiff
  });

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  type Token = { value: string; start: number; end: number };
  const tokenize = (value: string): Token[] => {
    const out: Token[] = [];
    const re = /[A-Za-z0-9']+/g;
    let m: RegExpExecArray | null = re.exec(value);
    while (m) {
      out.push({
        value: m[0],
        start: m.index,
        end: m.index + m[0].length
      });
      m = re.exec(value);
    }
    return out;
  };

  const normalizeToken = (value: string) => value.toLowerCase();

  const findOrderedTokenIndexes = (phraseText: string, keywordText: string): Set<number> | null => {
    const phraseTokens = tokenize(phraseText);
    const keywordTokens = tokenize(keywordText).map((t) => normalizeToken(t.value));
    if (!phraseTokens.length || keywordTokens.length < 2) {
      return null;
    }

    let best: number[] | null = null;

    for (let i = 0; i < phraseTokens.length; i++) {
      if (normalizeToken(phraseTokens[i].value) !== keywordTokens[0]) {
        continue;
      }
      const picked = [i];
      let cursor = i + 1;
      let ok = true;
      for (let k = 1; k < keywordTokens.length; k++) {
        let found = -1;
        for (let p = cursor; p < phraseTokens.length; p++) {
          if (normalizeToken(phraseTokens[p].value) === keywordTokens[k]) {
            found = p;
            break;
          }
        }
        if (found === -1) {
          ok = false;
          break;
        }
        picked.push(found);
        cursor = found + 1;
      }
      if (!ok) {
        continue;
      }
      if (!best || (picked[picked.length - 1] - picked[0]) < (best[best.length - 1] - best[0])) {
        best = picked;
      }
    }

    return best ? new Set(best) : null;
  };

  const renderByTokenIndexes = (phraseText: string, tokenIndexes: Set<number>) => {
    const phraseTokens = tokenize(phraseText);
    if (!phraseTokens.length) {
      return <span>{phraseText}</span>;
    }

    const nodes: ReactElement[] = [];
    let cursor = 0;
    for (let i = 0; i < phraseTokens.length; i++) {
      const token = phraseTokens[i];
      if (token.start > cursor) {
        nodes.push(<span key={`t-${i}-sep`}>{phraseText.slice(cursor, token.start)}</span>);
      }
      const chunk = phraseText.slice(token.start, token.end);
      nodes.push(
        tokenIndexes.has(i)
          ? <strong key={`t-${i}-w`}>{chunk}</strong>
          : <span key={`t-${i}-w`}>{chunk}</span>
      );
      cursor = token.end;
    }
    if (cursor < phraseText.length) {
      nodes.push(<span key="tail">{phraseText.slice(cursor)}</span>);
    }
    return nodes;
  };

  // Highlight exact phrase first; fallback to ordered token highlight with gaps allowed.
  const highlightKeyword = (phraseText: string, keywordText: string) => {
    const cleanKeyword = `${keywordText || ''}`.trim();
    if (!cleanKeyword) {
      return <span>{phraseText}</span>;
    }

    const exact = new RegExp(`\\b${escapeRegExp(cleanKeyword)}\\b`, 'i');
    const exactMatch = phraseText.match(exact);
    if (exactMatch?.index != null) {
      const start = exactMatch.index;
      const end = start + exactMatch[0].length;
      return (
        <>
          <span>{phraseText.slice(0, start)}</span>
          <strong>{phraseText.slice(start, end)}</strong>
          <span>{phraseText.slice(end)}</span>
        </>
      );
    }

    const tokenIndexes = findOrderedTokenIndexes(phraseText, cleanKeyword);
    if (!tokenIndexes) {
      return <span>{phraseText}</span>;
    }
    return renderByTokenIndexes(phraseText, tokenIndexes);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    const action = (e.target as HTMLElement).dataset.action;
    currentAction.current = (action as CardAction) || CardAction.TAP;
  };

  return (
    <div className={`swipestack-card-container ${active ? 'swipestack-card-container__active' : ''}`} onPointerDown={handlePointerDown}>
      <animated.div
        className="card-front"
        style={{
          opacity: opacity.to(o => 1 - o),
          transform,
          visibility: opacity.to((o) => (1 - o === 0 ? 'hidden' : 'visible'))
        }}
      >
        <div className="card-content">
          <h2 className="word-title">{word}</h2>
          <h3 className="transcription">
            [{transcription}]
          </h3>
          <p className="definition">
            {definition}
          </p>
          <div className="timestamps">
            {uniqueTss.map((ts) => (<Timestamp ts={ts} key={ts} />))}
          </div>
        </div>
        {wordInfoVisible && shortInfo && (
          <animated.div className="word-info" style={wordInfoSpring}>
            <MediaInfoLite media={shortInfo} className="word-media-info" />
          </animated.div>
        )}
        <a className={`ic ${data.saved ? 'ic-bookmark-star disabled' : 'ic-bookmark-plus'} card-btn favorite-btn`} data-action={CardAction.SAVE}></a>
        <a className="ic ic-info card-btn info-btn" data-action={CardAction.INFO}></a>
      </animated.div>
      <animated.div
        className="card-back"
        style={{
          opacity,
          transform,
          rotateX: '180deg',
          visibility: opacity.to((o) => (o === 0 ? 'hidden' : 'visible'))
        }}
      >
        {examplePhrases && examplePhrases.length > 0 ? (
          <div className="phrases-container">
            {examplePhrases.map((phrase) => (
              <div key={`${phrase.id}`} className="phrase-card">
                <p className="phrase-text">
                  {highlightKeyword(phrase.phrase, word)} <Timestamp ts={phrase.ts} />
                </p>
                <p className="phrase-explanation">
                  {phrase.explanation}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p>No example phrases available.</p>
        )}
      </animated.div>
    </div>
  );
}
